/* tslint:disable:max-file-line-count */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { ToolbarType, UitkToolbar } from 'uitk-react-toolbar';
import { UitkDialogContent, UitkFullscreenDialog } from 'uitk-react-dialog';

import { AnalyticsStore, ContextStore } from 'bernie-core';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { FindAnswersFormLaunchLocation, FindAnswersStore } from 'stores/find-answers-store';

import { PropertyReviewType, Reviews } from '__generated__/api/types';
import { ReviewsFilterOptionsModel } from 'models/common';
import { ReviewsFilterTypes } from 'constants/reviews-filters-type';

import { DialogContent, DialogContentSplitView } from 'components/infosite/property-details/reviews-dialog-content';

import _SortAndFilter = Reviews._SortAndFilter;
import RatingCounts = Reviews.RatingCounts;

export interface ReviewsDialogProps extends LocalizedComponentProps {
  currentHotel?: CurrentHotelStore;
  context?: ContextStore;
  analytics?: AnalyticsStore;
  min?: number;
  onDismiss?: () => void;
  value?: number;
  onStickyBookButtonClick?: () => void;
  findAnswersStore?: FindAnswersStore;
  splitView?: boolean;
  fullScreen?: boolean;
}

export interface ReviewsDialogState {
  showDialog: boolean;
  showRatingFilterSheet: boolean;
  showTravelerFilterSheet: boolean;
}

@withLocalization
@inject('currentHotel', 'context', 'analytics', 'findAnswersStore')
@observer
export class ReviewsDialog extends React.Component<ReviewsDialogProps, ReviewsDialogState> {
  public constructor(props: ReviewsDialogProps) {
    super(props);

    this.state = {
      showDialog: true,
      showRatingFilterSheet: false,
      showTravelerFilterSheet: false
    };

    this.onDismiss = this.onDismiss.bind(this);
    this.onMoreReviews = this.onMoreReviews.bind(this);
    this.applyRatingsFilters = this.applyRatingsFilters.bind(this);
    this.applyTravelerTypeFilters = this.applyTravelerTypeFilters.bind(this);
    this.toggleTravelerFilterSheet = this.toggleTravelerFilterSheet.bind(this);
    this.toggleRatingFilterSheet = this.toggleRatingFilterSheet.bind(this);
    this.applyCommonPhrasesFilters = this.applyCommonPhrasesFilters.bind(this);

    if (props.findAnswersStore) {
      props.findAnswersStore.setFormLaunchLocation(FindAnswersFormLaunchLocation.REVIEWS_DIALOG);
    }
  }

  public componentWillUnmount() {
    const { findAnswersStore } = this.props;
    if (findAnswersStore) {
      findAnswersStore.setFormLaunchLocation(FindAnswersFormLaunchLocation.INFOSITE);
    }
  }

  public applyRatingsFilters(filters: number[], shouldFetch?: boolean): void {
    const { context, currentHotel } = this.props;
    const scoresInNumberType = filters.map(score => Number(score));

    currentHotel.setReviewsScoreFilter(scoresInNumberType);

    if (shouldFetch) {
      currentHotel.fetchFilteredReviews(context, currentHotel.hotelId, true);
    }
  }

  public applyCommonPhrasesFilters = (keyphrase: string, shouldFetch?: boolean): void => {
    const { context, currentHotel } = this.props;

    currentHotel.setSearchTermFilters(keyphrase);

    if (shouldFetch) {
      currentHotel.fetchFilteredReviews(context, currentHotel.hotelId, true);
    }
  };

  public applyTravelerTypeFilters(filters: string[], shouldFetch?: boolean): void {
    const { context, currentHotel } = this.props;

    currentHotel.setTravelerTypeFilters(filters);

    if (shouldFetch) {
      currentHotel.fetchFilteredReviews(context, currentHotel.hotelId, true);
    }
  }

  private getRatingCounts(ratingValue: number, allRatingCounts: RatingCounts[]) {
    const matchedRating = allRatingCounts && allRatingCounts.find(ratingData => ratingData.rating === ratingValue);

    return (matchedRating && matchedRating.count && matchedRating.count.formatted) || '0';
  }

  public toggleRatingFilterSheet(): void {
    this.setState(prevState => ({
      showRatingFilterSheet: !prevState.showRatingFilterSheet,
      showTravelerFilterSheet: false
    }));
  }

  public toggleTravelerFilterSheet(): void {
    this.setState(prevState => ({
      showTravelerFilterSheet: !prevState.showTravelerFilterSheet,
      showRatingFilterSheet: false
    }));
  }

  public onMoreReviews(includeRatingsOnlyReview?: boolean, reviewType?: PropertyReviewType): void {
    const { context, currentHotel } = this.props;
    const filtersApplied = currentHotel && currentHotel.checkReviewsFiltersApplied;

    if (filtersApplied) {
      currentHotel.fetchFilteredReviews(context, currentHotel.hotelId, includeRatingsOnlyReview);
    } else {
      currentHotel.fetchReviews(context, currentHotel.hotelId, includeRatingsOnlyReview, reviewType);
    }
  }

  public onDismiss(): void {
    const { findAnswersStore, onDismiss, currentHotel } = this.props;
    if (onDismiss) {
      onDismiss();
      currentHotel.changeReviewsFiltersCheckedStatus();
    }
    if (findAnswersStore) {
      findAnswersStore.setFormLaunchLocation(FindAnswersFormLaunchLocation.INFOSITE);
    }
  }

  public getFilterOptions = (sortAndFilter: _SortAndFilter[], filterType: string): ReviewsFilterOptionsModel[] => {
    if (sortAndFilter && filterType) {
      const filterOptions = [];
      sortAndFilter.map(filter => {
        if (filter.name === filterType) {
          filter.options.map(option => {
            filterOptions.push({
              value: option.optionValue,
              label: option.label,
              trackingId: `${filter.label}.${option.label}`,
              filterName: filter.label,
              selected: option.isSelected
            });
          });
        }
      });

      return filterOptions;
    } else {
      return null;
    }
  };

  public render() {
    const { showDialog, showRatingFilterSheet, showTravelerFilterSheet } = this.state;
    const { l10n, analytics, context, currentHotel, fullScreen, splitView } = this.props;
    const { reviews, positiveReviews, criticalReviews, hotel, reviewScoreFilters, sortAndFilter } = currentHotel;
    const { ratingCounts } = reviews;
    const questionAnswersViewModel = hotel && hotel.questionAnswersViewModel;
    const toolbarAnalytics = analytics.setupTracker('ReviewsDialog', 'close reviews');
    const toolBarAttributes = {
      type: ToolbarType.CLOSE,
      header: <LocalizedText message="hotels.infosite.reviews.title" />,
      iconLabel: l10n.formatText('hotels.infosite.reviews.cancelButton.a11yLabel')
    };

    const ratingFiltersOptions = [
      {
        value: 5,
        label: `5 - ${l10n.formatText('hotels.infosite.reviews.superlative.excellent')} (${this.getRatingCounts(5, ratingCounts)})`,
        selected: reviewScoreFilters && reviewScoreFilters.includes(5),
        trackingId: 'Rating5'
      },
      {
        value: 4,
        label: `4 - ${l10n.formatText('hotels.infosite.reviews.superlative.good')} (${this.getRatingCounts(4, ratingCounts)})`,
        selected: reviewScoreFilters && reviewScoreFilters.includes(4),
        trackingId: 'Rating4'
      },
      {
        value: 3,
        label: `3 - ${l10n.formatText('hotels.infosite.reviews.superlative.okay')} (${this.getRatingCounts(3, ratingCounts)})`,
        selected: reviewScoreFilters && reviewScoreFilters.includes(3),
        trackingId: 'Rating3'
      },
      {
        value: 2,
        label: `2 - ${l10n.formatText('hotels.infosite.reviews.superlative.mediocre')} (${this.getRatingCounts(2, ratingCounts)})`,
        selected: reviewScoreFilters && reviewScoreFilters.includes(2),
        trackingId: 'Rating2'
      },
      {
        value: 1,
        label: `1 - ${l10n.formatText('hotels.infosite.reviews.superlative.poor')} (${this.getRatingCounts(1, ratingCounts)})`,
        selected: reviewScoreFilters && reviewScoreFilters.includes(1),
        trackingId: 'Rating1'
      }
    ];
    const ratingFilterCount = ratingFiltersOptions.filter(option => option.selected === true).length;

    const travelerTypeFilterOptions = this.getFilterOptions(sortAndFilter, ReviewsFilterTypes.TRAVELER_TYPE);
    const travelerFilterCount = travelerTypeFilterOptions.filter(option => option.selected === true).length;

    const dialogContentProps = {
      context,
      reviews,
      l10n,
      questionAnswersViewModel,
      onMoreReviews: this.onMoreReviews,
      positiveReviews,
      criticalReviews,
      applyRatingsFilters: this.applyRatingsFilters,
      applyTravelerTypeFilters: this.applyTravelerTypeFilters,
      propertyId: currentHotel.hotelId,
      applyCommonPhrasesFilters: this.applyCommonPhrasesFilters,
      ratingFiltersOptions,
      travelerTypeFilterOptions,
      ratingFilterCount,
      travelerFilterCount,
      showRatingFilterSheet,
      showTravelerFilterSheet,
      toggleTravelerFilterSheet: this.toggleTravelerFilterSheet,
      toggleRatingFilterSheet: this.toggleRatingFilterSheet
    };

    return (
      <UitkFullscreenDialog dialogShow={showDialog} strictFullscreen={fullScreen}>
        <UitkToolbar {...toolBarAttributes} analytics={toolbarAnalytics} action={this.onDismiss} />
        <UitkDialogContent className="reviews-dialog-main">
          {!splitView && <DialogContent {...dialogContentProps} />}
          {splitView && <DialogContentSplitView {...dialogContentProps} />}
        </UitkDialogContent>
      </UitkFullscreenDialog>
    );
  }
}
